import { Vue, Options } from 'vue-class-component';
import { mapState } from 'vuex';
import {
  ApartmentDTO,
  EntrancePlantDetailDTO,
  ApartmentInvitationDTO,
  ApartmentLabelDTO,
  AddressingType,
} from '@/../client-generator/generated-clients/plants';
import { PropType } from 'vue';
import { getUserInitialsForBubble } from '@/core/utils/utils';
import DialogEditLabel from './DialogEditLabel/DialogEditLabel.vue';
import DialogInviteResident from './DialogInviteResident/DialogInviteResident.vue';
import { SET_APP_LOADING } from '@/core/store/action-types';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';
import plantsClient from '@/core/services/plants';
import DialogMaxEntity from '@/views/components/DialogMaxEntity/DialogMaxEntity.vue';

@Options({
  name: 'ContactSection',
  components: {
    DialogEditLabel,
    DialogMaxEntity,
    DialogConfirmDelete,
    DialogInviteResident,
  },
  props: {
    apartmentSelected: {
      type: Object as PropType<ApartmentDTO>,
      required: true,
    },
    editingApartment: {
      type: Object as PropType<ApartmentDTO>,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant', 'adminView']),
  },
  watch: {
    'editingApartment.scsAddr': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.buildingCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.floorCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.riserCode': function () {
      this.apartmentWasEdited();
    },
    'editingApartment.apartmentCode': function () {
      this.apartmentWasEdited();
    },
  },
  emits: ['onAddEditLabel', 'onDeleteLabel', 'update-list-invites', 'contact-edited', 'error-contact'],
})
export default class ContactSection extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  adminView!: boolean;
  editingApartment!: ApartmentDTO;
  apartmentSelected!: ApartmentDTO;
  labelToEdit: ApartmentLabelDTO | null = null;
  invitationToRemove: ApartmentInvitationDTO | null = null;
  isVisibleDialogEditLabel: boolean = false;
  isVisibleDialogInviteResident: boolean = false;
  isEdit: boolean = false;
  isVisibleDeleteInviteConfirm: boolean = false;
  isVisibleMaxEntity: boolean = false;

  readonly MIN_SCS_ADDRESS = 1;
  readonly MAX_SCS_ADDRESS = 3999;

  scsAddrNotValid: boolean = false;
  invalidScsReason = '';

  get disableAddLabel() {
    return !this.currentEntrancePlant.syncStatus?.canEditWithPropagation;
  }

  get isFloorsAddressing(): boolean {
    return this.currentEntrancePlant.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  get isCallWithCode(): boolean {
    return (
      this.currentEntrancePlant.addressingType ===
        AddressingType.DirectCall_Alphanumeric ||
      this.currentEntrancePlant.addressingType ===
        AddressingType.DirectCall_NumbersOnly
    );  
  }

  get currentInvitations() {
    return (
      this.currentEntrancePlant.apartmentInvitations?.filter(
        (i) => i.apartmentId === this.apartmentSelected.apartmentId ) ?? []
    );
  }

  get getContactError() {
    this.verifyValidityOfScsAddress();
    return this.scsAddrNotValid;
  }

  get disableInvite() {
    return !this.currentEntrancePlant.syncStatus?.isPlantActive || this.currentInvitations.length  >= 1;
  }

  get revokeInviteMessage() {
    const hasCode = this.editingApartment.keypadCode || this.editingApartment.keypadCode !== '';
    if (hasCode) {
      return this.$t('AppResourceEP.ConfirmRemoveInvitedUser');
    }
    else{
      return this.$t('AppResourceEP.ConfirmRemoveInvitedUser').split('.')[0] + '.';
    }
  }

  getLabelText(label: ApartmentLabelDTO) {
    if (this.isFloorsAddressing) {
      return label.callingCode;
    }

    if (this.isCallWithCode) {
      let labelText = label.callingCode ?? '';

      if (label.isPrivate && (label.surname || label.name)) {
        labelText += ` |`;
        labelText += ` ${label.surname ?? ''}`;
        labelText += ` ${label.name ?? ''}`;
      } else if (!label.isPrivate && label.companyName) {
        labelText += ` | ${label.companyName ?? ''}`;
      }

      return labelText;
    }

    if (label.isPrivate) {
      return `${label.surname ?? ''} ${label.name ?? ''}`;
    } else {
      return label.companyName;
    }
  }

  mounted(): void {
  }

  checkIfCanAddLabel() {
    if (this.editingApartment.labels!.length >= 10) {
      this.isVisibleMaxEntity = true;
      return;
    }
    this.onAddLabelClick();
  }

  toggleMaxEntityDialog() {
    this.isVisibleMaxEntity = !this.isVisibleMaxEntity;
  }

  onAddLabelClick() {
    this.labelToEdit = null;
    this.isEdit = false;
    this.isVisibleDialogEditLabel = true;
  }

  onEditLabelClick(label: ApartmentLabelDTO) {
    this.labelToEdit = label;
    this.isEdit = true;
    this.isVisibleDialogEditLabel = true;
  }

  onDeleteLabelClick(labelId: string) {
    this.$emit('onDeleteLabel', labelId);
  }

  async onSaveLabel(newLabel: ApartmentLabelDTO) {
    this.hideLabelDialog();
    this.$emit('onAddEditLabel', newLabel);
  }

  userInitials(invitation: ApartmentInvitationDTO): string {
    return getUserInitialsForBubble(invitation);
  }

  hideLabelDialog() {
    this.isVisibleDialogEditLabel = false;
  }

  toggleInviteDialog() {
    this.isVisibleDialogInviteResident = !this.isVisibleDialogInviteResident;
  }

  toggleDeleteInviteConfirm(invitation: ApartmentInvitationDTO) {
    this.invitationToRemove = invitation;
    this.isVisibleDeleteInviteConfirm = !this.isVisibleDeleteInviteConfirm;
  }

  async handleDeleteInvite() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      //remove inviteToRemove
      await plantsClient.residentinvitationDELETE(this.currentEntrancePlant.desktopId!, this.invitationToRemove!);
    } finally {
      this.handleUpdateList();
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleUpdateList() {
    this.$emit('update-list-invites');
  }

  apartmentWasEdited() {
    this.$emit('error-contact', this.getContactError);
    this.$emit('contact-edited', this.contactEdited());
  }

  contactEdited() {
    const scsEdit = this.editingApartment.scsAddr != this.apartmentSelected.scsAddr;
    if(this.isFloorsAddressing) {
      const buildingCodeEdit = this.editingApartment.buildingCode != this.apartmentSelected.buildingCode;
      const floorCodeEdit = this.editingApartment.floorCode != this.apartmentSelected.floorCode;
      const riserCodeEdit = this.editingApartment.riserCode != this.apartmentSelected.riserCode;
      const apartmentCodeEdit = this.editingApartment.apartmentCode != this.apartmentSelected.apartmentCode;
      const contactEdit = scsEdit || buildingCodeEdit || floorCodeEdit || riserCodeEdit || apartmentCodeEdit;
      return contactEdit;
    }
    return scsEdit;
  }

  verifyValidityOfScsAddress() {      
    const value = Number(this.editingApartment.scsAddr);
    let acceptedRange = false;
    let doesNotExist = false;

    acceptedRange = value >= this.MIN_SCS_ADDRESS && value <= this.MAX_SCS_ADDRESS;
    if (!acceptedRange) {
      this.scsAddrNotValid = true;
      this.invalidScsReason = this.$t('AppResourceEP.InputNotValid');
      return;
    }

    let listToCheck = this.currentEntrancePlant.buildings!
          .flatMap((b) => b.apartments || [])
    listToCheck = listToCheck.filter((a) => a.apartmentId !== this.editingApartment.apartmentId);
    doesNotExist = !listToCheck.map((a) => a.scsAddr).includes(value);

    if (!doesNotExist) {
      this.scsAddrNotValid = true;
      this.invalidScsReason = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
    } else {
      this.scsAddrNotValid = false;
      this.invalidScsReason = '';
    }
  }
}
